/*
  Upgrade — Sell quote engine
  ---------------------------------------------------------------------------
  Same four customer questions as the Trade-in tool (device / condition /
  functional fault / battery), answered from the SAME published offer table,
  in the same file: assets/upgrade-tradein-data.json.

  The offer is LOOKED UP, not calculated. Ruling of 22 September 2026: the
  browser is given outcomes, never inputs. assets/ is served with no
  authentication, so the price Upgrade pays for a handset and the component
  amounts behind an offer are not in this file, not in the data it fetches,
  and not recoverable from anything it renders. What ships is one precomputed
  offer per combination, plus a band code that also carries the refusal.

  The contract is published in meta.quote_contract and implemented in quote()
  below: index the variant's row by the condition, battery and fault answers,
  then read the cash figure and the band letter out of it. A null figure, or
  the refusal band, means no number goes on screen.

  This file is intentionally a separate, independent implementation of that
  lookup -- it does not import or modify assets/upgrade-tradein.js. The two
  engines must keep returning the same cash figure for the same four
  answers; if the offer table ever changes shape, both files need to be
  checked, not just one.

  Where this differs from the Trade-in engine, by design:
  - The PRIMARY outcome is a cash offer, and it stays complete on its own:
    its own figure, its own working, its own booking button. Selling outright
    is a real choice, not a detour into a purchase.
  - Below that complete sell journey the result screen also shows what the
    same phone is worth TRADED IN -- the identical valuation with the
    trade-up bonus added to it, paid as credit. That is the one comparison
    the bonus exists to drive, and the customer cannot make the choice
    without seeing both sides of it. See "the bridge into trade-in" below;
    the bonus is never stated until a real catalogue device is picked.
  - The result screen ends in a booking CTA, not an "Add to cart" upsell.
    Since 28 Sep 2026 (LOG-021) the booking is the sell's alone: a trade-in
    ends in an order for the new phone, and a sell ends in a booked visit.
  - No grade-estimate is computed or shown. Grade is a graded-INTAKE
    concept for Upgrade's own stock, not a number a seller needs to see,
    and the grade/battery-threshold definitions are still unreconciled
    per 00-COMPANY/05-products-and-grading.md -- nothing here states one.
  ---------------------------------------------------------------------------
*/
(function () {
  'use strict';
  /* ---- ARABIC PHASE 3, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/phase3-tradein-sell.md)
     Every customer word this tool prints is in EN below, exactly as it was
     written inline before, read through T(). An English page has no
     dictionary, so T() returns EN's text and the English output is
     identical by construction (tests/engines.test.js, one-price,
     tradeup-balance and totals run this file with no window.UpgradeI18n).
     On an Arabic page T() reads window.UpgradeI18n, falling back to EN per
     key. The four valuation questions use the same words as
     assets/upgrade-tradein.js in English; their Arabic is drafted identical
     too (tests/i18n.test.js checks it), so the same customer reads the same
     questions on either page.
     STAYS ENGLISH, in every language: the valuation handed to
     window.UpgradeDeal.setTradein() (keys and numbers), the target written by
     setTarget() (label, gradeLabel, storage), the console diagnostics, and
     the PREFILLED WHATSAPP MESSAGES TO STAFF (buildContact() /
     sellEnquiryMessage(): staff-facing and dormant -- the WhatsApp number is
     blank, LOG-033; whether they follow the customer's language is open,
     plan decision #10, vale-cro / halberg-coo). The dormant WhatsApp labels
     are in EN but kept English on purpose (i18n/ar-english-kept.json).
     FIGURES: fig() / money() / plusFig() as in assets/upgrade-tradein.js.
     LINKS: routeRoot() keeps the language prefix. */
  var EN = {
    'common.grade_g': 'Gold',
    'common.grade_p': 'Platinum',
    'common.grade_d': 'Diamond',
    'common.money': '{amount} EGP',
    'common.currency': 'EGP',
    'sell.cond_flawless_title': 'Flawless',
    'sell.cond_flawless_desc': 'No scratches, no dents. Looks unused.',
    'sell.cond_good_title': 'Good',
    'sell.cond_good_desc': 'Light marks on the screen, frame or back. No dents.',
    'sell.cond_fair_title': 'Fair',
    'sell.cond_fair_desc': 'Heavy scratches or small dents. Everything works.',
    'sell.cond_damaged_title': 'Damaged',
    'sell.cond_damaged_desc': 'Cracked screen or broken body.',
    'sell.batt_96plus': '96% or more',
    'sell.batt_91to95': '91–95%',
    'sell.batt_90orless': '90% or less',
    'sell.batt_unsure': "Don't know",
    'sell.priced_batt_unsure': 'battery health not known',
    'sell.priced_batt': 'battery {battery}',
    'sell.priced_batt_not_stated': 'battery health not stated',
    'sell.priced_summary': '{phone}, {condition} condition, {battery}, {fault}',
    'sell.priced_fault_yes': 'a functional fault',
    'sell.priced_fault_no': 'no faults',
    'sell.browse_title': 'Want a different phone?',
    'sell.browse_note': 'Pick any device we sell. Your valuation follows you, so you are not asked these questions again.',
    'sell.browse_every_device': 'Browse every device we have',
    'sell.bridge_title': 'Or trade it in instead',
    'sell.bridge_no_rate': 'You can also put your {device} toward a refurbished phone instead of taking the cash.',
    'sell.bridge_explainer': 'Trade in and we add a trade-up bonus to what your phone is worth, and pay the total as credit against the device you buy. The bonus is {pct} of that device\'s price, capped at {cap} EGP. It is set by the device you choose and by nothing else – the condition, battery and age of your {device} do not change it.',
    'sell.bridge_pick': 'Pick the device you would trade up to:',
    'sell.bridge_pick_label': 'Device to trade up to',
    'sell.bridge_row_value': 'Your {phone} — estimate, confirmed at delivery',
    'sell.bridge_row_bonus': 'Trade-up bonus – guaranteed, fixed by the device you choose',
    'sell.bridge_row_credit': 'Total credit',
    'sell.bridge_row_target': '{device} – {grade}',
    'sell.bridge_row_balance': 'Estimated balance to pay',
    'sell.bridge_note': 'Only the value of your phone is an estimate, which is why the balance is one too. The {bonus} EGP bonus is not: it is fixed by the {target} and does not move. Order the {target_model} with your trade-in: when we deliver it, we check your {phone} in front of you – if it matches what you told us, the balance above is what you pay. The cart shows the full price; nothing is paid until delivery.',
    'sell.bridge_cta': 'Trade up to the {device}',
    'sell.step_device': 'Device',
    'sell.step_condition': 'Condition',
    'sell.step_battery': 'Battery',
    'sell.step_offer': 'Cash offer',
    'sell.device_title': 'Which device are you selling?',
    'sell.brand_label': 'Brand',
    'sell.model_label': 'Model',
    'sell.whatsapp_demo_title': 'Demo build: this WhatsApp number is a placeholder pending confirmation by marketing ops, not a verified Upgrade contact.',
    'sell.whatsapp_link_demo': 'Message us on WhatsApp <sup class="ug-demo-flag">demo</sup>',
    'sell.call_our_hotline': 'Call our hotline, {hotline}',
    'sell.another_model': 'Another model? {link} and we will quote it by hand.',
    'sell.storage_label': 'Storage',
    'sell.condition_title': 'What condition is it in?',
    'sell.condition_label': 'Condition',
    'sell.fault_question': 'Separately — does it have a functional fault that is not about looks (for example: speaker, microphone, camera, buttons, or charging)?',
    'sell.fault_label': 'Functional fault',
    'sell.fault_no': 'No, everything works',
    'sell.fault_yes': 'Yes, it has a fault',
    'sell.battery_help_samsung': "Samsung phones don't show a percentage &mdash; choose Don't know.",
    'sell.battery_help_iphone15': 'iPhone: Settings &rarr; Battery &rarr; Battery Health.',
    'sell.battery_help_iphone14': 'iPhone: Settings &rarr; Battery &rarr; Battery Health &amp; Charging.',
    'sell.battery_title': 'Battery health?',
    'sell.battery_label': 'Battery health',
    'sell.offer_title': 'Your cash offer',
    'sell.select_storage': 'Select a storage size to see your offer.',
    'sell.refused_title': 'We need to see this one first',
    'sell.refused_fault': "We don't price a phone with a fault online, so your {phone} needs to be seen first. Rather than show you a number we might not honour, one of our buyers will price it by hand.",
    'sell.refused_floor': 'Your {phone}, in the condition you described, falls below the lowest offer we will quote without seeing the phone. Rather than show you a number we might not honour, one of our buyers will price it by hand.',
    'sell.offer_label': 'Cash for your {phone}',
    'sell.offer_sub': 'Paid at the visit, in cash or by transfer, once we have checked your phone.',
    'sell.offer_compare_note': 'That is the cash price. Trade it in instead and we add a trade-up bonus to the same value, paid as credit against your next phone – the comparison is below.',
    'sell.what_we_priced': 'What we priced',
    'sell.working_note': 'What we priced: {summary}.<br>Confirmed when we check your phone at the visit – if it matches what you told us, the price above is the price you are paid.',
    'sell.book_cta': 'Book my sale',
    'sell.book_whatsapp_demo': 'Book my sale on WhatsApp <sup class="ug-demo-flag">demo</sup>',
    'sell.book_call_cta': 'Book my sale — call {hotline}',
    'sell.payment_note': 'We pay you at the visit, in cash or by bank transfer, whichever you prefer. Book a collection from your address, or bring it to our Sheikh Zayed branch.',
    'sell.start_again': 'Start again',
    'sell.pricing_unavailable': 'Sell pricing is unavailable right now.',
    /* ARABIC PORT pA, 7 Oct 2026 (2026-10-04_arabic/port-2026-10-07/pA.md).
       - The 12 words the English build moved into section settings (Lynkrs
         batch 2, W1b: tool_device_title ... tool_reset_label, data-t-*
         attributes read through TX()) KEEP their keys above: they are TEXT's
         words below, what TX() falls back to in the page's language. The
         Arabic of the settings themselves is theme content (rule R1;
         port-2026-10-07/pA-theme-content.json).
       - Six sentences above carry the batch-2 en dash (D2).
       - The step count is new (Q6/D1); two numbers, so a key (rule R4). */
    'sell.step_count': 'Step {n} of {total}'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  // {name} -> value; an unknown {name} is left as it is.
  function fill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  // The dictionary's text when it has the key, else EN's. Never throws.
  // Edit everything (7 Oct 2026): first, the content team's word for the
  // key, when the section hands one over that may be used (wordFor(), below
  // the mount). Escaped; a new line she typed becomes <br>.
  function T(key, vars) {
    var w = wordFor(key);
    if (w !== null) { return fillWords(escWords(w).replace(/\n/g, '<br>'), vars); }
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return fill(EN[key], vars);
  }
  // The same word for a place that takes plain text (an aria-label, a
  // <summary>'s textContent): not escaped, new lines read as spaces.
  function TP(key, vars) {
    var w = wordFor(key);
    if (w !== null) { return fillWords(w.replace(/\s*\n\s*/g, ' '), vars); }
    return T(key, vars);
  }
  function rtl() {
    try { return !!(I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl'); } catch (e) { return false; }
  }
  // Bidi isolation of a value set as text, on right-to-left pages only.
  function isolateText(value) {
    var str = String(value == null ? '' : value);
    try {
      if (rtl() && typeof I18N.isolate === 'function') { return I18N.isolate(str); }
    } catch (e) { /* English behaviour */ }
    return str;
  }
  // A DISPLAY figure (Western digits on Arabic, LOG-043; isolated on RTL,
  // `sign` inside the isolate). English: egp(n), exactly.
  function fig(n, sign) {
    var s = sign || '';
    if (I18N && typeof I18N.num === 'function') {
      var d = null;
      try { d = I18N.num(Math.round(n || 0)); } catch (e) { d = null; }
      if (typeof d === 'string') { return isolateText(s + d); }
    }
    return s + egp(n);
  }
  // The bonus as an added figure. English: '+ ' + egp(n), exactly as before.
  function plusFig(n) {
    if (I18N && typeof I18N.num === 'function') { return fig(n, '+'); }
    return '+ ' + egp(n);
  }
  // The bonus RATE as a percent (one decimal at most, "10" or "7.5"), with
  // its % sign. English: exactly the text it always printed. On a translated
  // page the figure WITH its % sign is one isolate, so "10%" stays one unit
  // in right-to-left text (Western digits, LOG-043).
  function pctFig(pct) {
    var text = String(Math.round(pct * 1000) / 10) + '%';
    return I18N ? isolateText(text) : text;
  }
  // A grade's display name from its code (English: the GRADE_LABELS word).
  function gradeName(code) {
    return T('common.grade_' + code);
  }
  // The language prefix for links this file builds: '/' on English, '/ar/'
  // on Arabic (Shopify.routes.root). Same rule as assets/upgrade-deal.js.
  function routeRoot() {
    var r = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  /* ARABIC PORT pA, 7 Oct 2026: a site path from window.UpgradeRoutes
     (Lynkrs B6: Shopify's product.url, or '/pages/shop') keeps the language
     prefix on a translated page. A path already carrying it, an absolute or
     protocol-relative URL, and anything on an English page (routeRoot() is
     '/') are returned exactly as given. Same helper as upgrade-tradein.js. */
  function localHref(u) {
    var s = String(u == null ? '' : u);
    var r = routeRoot();
    if (r === '/' || s.charAt(0) !== '/' || s.charAt(1) === '/' || s.indexOf(r) === 0 || s + '/' === r) return s;
    return r + s.slice(1);
  }

  var root = document.querySelector('[data-upgrade-sell]');
  if (!root) return;

  /* ---- the rest of the tool's words (Edit everything, 7 Oct 2026) --------
     Omar, 7 Oct 2026: "my content team needs to be able to edit anything".
     55 more of the words in EN are settings of five blocks of the "Upgrade
     sell" section (Theme editor > Upgrade sell > "Tool words: 1 Device" ...
     "Tool words: 5 Trade-in"; sections/upgrade-sell.liquid). The section
     prints each block's words as one JSON object, in a
     <script type="application/json" data-ug-sell-words> beside the mount
     (never inside it: render() empties the mount), keyed by the EN name
     without "sell." (the setting id). T() and TP() use such a word only
     when ALL of these hold, and otherwise do exactly what they did before
     (EN on an English page, the dictionary on /ar):
       - the key is one of WORD_KEYS: never a battery band (its numbers are
         the offer's axis), a grade name, the money format or a dormant
         WhatsApp word (LOG-044; sellEnquiryMessage() reads EN directly);
       - the value is not blank (an emptied field shows the standard words);
       - it carries the same {placeholders} as EN, so no figure is lost and
         no raw {token} reaches the page;
       - it differs from EN once EN's <br> and entities are read as text.
         So an English page she has not edited prints EN, byte for byte;
         and on /ar a value Shopify serves untranslated (the English)
         gives way to the Arabic dictionary, while an Arabic translation
         registered in Translate & Adapt differs, and is used.
     Her words are text: escaped where markup is written, a new line is
     <br>, and on a right-to-left page each filled-in value is isolated, as
     the dictionary's own t() does. Read once per run; the theme editor's
     re-render runs this file again (shopify:section:load, foot of file).
     Null-safe: no document.querySelectorAll, no element, bad JSON -> {}. */
  var WORD_KEYS = {
    'sell.step_device': 1, 'sell.step_count': 1, 'sell.brand_label': 1, 'sell.model_label': 1, 'sell.storage_label': 1,
    'sell.another_model': 1, 'sell.call_our_hotline': 1,
    'sell.step_condition': 1, 'sell.cond_flawless_title': 1, 'sell.cond_flawless_desc': 1, 'sell.cond_good_title': 1,
    'sell.cond_good_desc': 1, 'sell.cond_fair_title': 1, 'sell.cond_fair_desc': 1, 'sell.cond_damaged_title': 1,
    'sell.cond_damaged_desc': 1, 'sell.condition_label': 1, 'sell.fault_question': 1, 'sell.fault_label': 1,
    'sell.fault_no': 1, 'sell.fault_yes': 1,
    'sell.step_battery': 1, 'sell.batt_unsure': 1, 'sell.battery_help_samsung': 1, 'sell.battery_help_iphone15': 1,
    'sell.battery_help_iphone14': 1, 'sell.battery_label': 1,
    'sell.step_offer': 1, 'sell.select_storage': 1, 'sell.offer_label': 1, 'sell.offer_compare_note': 1,
    'sell.what_we_priced': 1, 'sell.working_note': 1, 'sell.priced_summary': 1, 'sell.priced_batt': 1,
    'sell.priced_batt_unsure': 1, 'sell.priced_batt_not_stated': 1, 'sell.priced_fault_yes': 1, 'sell.priced_fault_no': 1,
    'sell.book_call_cta': 1, 'sell.refused_title': 1, 'sell.refused_fault': 1, 'sell.refused_floor': 1,
    'sell.pricing_unavailable': 1,
    'sell.bridge_explainer': 1, 'sell.bridge_no_rate': 1, 'sell.bridge_pick': 1, 'sell.bridge_pick_label': 1,
    'sell.bridge_row_value': 1, 'sell.bridge_row_bonus': 1, 'sell.bridge_row_credit': 1, 'sell.bridge_row_target': 1,
    'sell.bridge_row_balance': 1, 'sell.bridge_note': 1, 'sell.bridge_cta': 1
  };
  var WORDS = readWords();
  function readWords() {
    var out = {};
    try {
      var nodes = null;
      if (typeof document.querySelectorAll === 'function') {
        nodes = document.querySelectorAll('script[data-ug-sell-words]');
      } else if (typeof document.querySelector === 'function') {
        var one = document.querySelector('script[data-ug-sell-words]');
        nodes = one ? [one] : null;
      }
      for (var i = 0; nodes && i < nodes.length; i++) {
        var o = null;
        try { o = JSON.parse(String((nodes[i] && nodes[i].textContent) || '')); } catch (e) { o = null; }
        if (!o || typeof o !== 'object') continue;
        for (var k in o) {
          if (Object.prototype.hasOwnProperty.call(o, k) && typeof o[k] === 'string') out['sell.' + k] = o[k];
        }
      }
    } catch (e) { return {}; }
    return out;
  }
  function tokensOf(s) {
    var seen = {}, out = [];
    String(s).replace(/\{(\w+)\}/g, function (whole, name) {
      if (!seen[name]) { seen[name] = 1; out.push(name); }
      return whole;
    });
    return out.sort().join(',');
  }
  // EN's markup read as text, for the comparison only.
  function asText(s) {
    return wsNorm(String(s).replace(/<br\s*\/?>/gi, '\n')
      .replace(/&mdash;/g, '—').replace(/&ndash;/g, '–').replace(/&rarr;/g, '→')
      .replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&'));
  }
  function wsNorm(s) {
    return String(s).replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').replace(/\n[ \t]+/g, '\n').replace(/^\s+|\s+$/g, '');
  }
  function wordFor(key) {
    if (!WORDS || !WORD_KEYS || !Object.prototype.hasOwnProperty.call(WORD_KEYS, key) ||
        !Object.prototype.hasOwnProperty.call(WORDS, key)) return null;
    var v = WORDS[key], en = EN[key];
    if (typeof v !== 'string' || !/\S/.test(v) || typeof en !== 'string') return null;
    if (tokensOf(v) !== tokensOf(en)) return null;
    v = wsNorm(v);
    return v === asText(en) ? null : v;
  }
  function escWords(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  // {name} -> value, each value isolated on a right-to-left page (English:
  // isolateText() returns it untouched), exactly as fill() otherwise.
  function fillWords(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? isolateText(vars[name]) : whole;
    });
  }

  var WA_NUMBER = (root.dataset.whatsapp || '').replace(/[^0-9]/g, '');
  // Upgrade's one confirmed, sourced contact number (00-COMPANY/01-company-
  // profile.md), editable via the section's "Confirmed hotline number"
  // setting (data-hotline). No verified WhatsApp Business number exists for
  // this demo (house rule: never invent one) -- every CTA below falls back
  // to this tel: link when WA_NUMBER is blank, instead of emitting a dead
  // https://wa.me/?text=... link with no destination number.
  var HOTLINE = (root.dataset.hotline || '19759').replace(/[^0-9]/g, '') || '19759';

  function buildContact(message) {
    if (WA_NUMBER) {
      return { href: 'https://wa.me/' + WA_NUMBER + '?text=' + encodeURIComponent(message), isWhatsapp: true };
    }
    return { href: 'tel:' + HOTLINE, isWhatsapp: false };
  }

  /* ---- words the content lead edits (Lynkrs batch 2, W1b, 6 Oct 2026) ----
     Batch E2. The tool's headings, buttons, the "different phone" box and
     the two payment lines are settings of the section (Theme editor >
     Upgrade sell > "Words in the valuation tool" / "Promises in the
     valuation tool"), handed over as data-t-* attributes on the mount
     (sections/upgrade-sell.liquid). TEXT is today's English, word for word:
     a missing attribute (an older section file, a test harness) reads it,
     so the page is unchanged until she edits. A heading or button she
     empties keeps these words; a note she empties (TEXT_LINES) is left out
     and TX() returns ''. What stays here and why: the answers and their
     descriptions, the battery help, the refusal, the offer, the trade-in
     sums and every sentence carrying a figure or the hotline are part of
     the price lookup or carry a number (Arabic rule R4).
     SUPERSEDED IN PART, 7 Oct 2026 (edit everything): those words are now
     editable too, as the "Tool words" blocks (WORDS, near the top); only
     the battery bands, grade names, money format and dormant WhatsApp
     words stay here alone. The figures themselves never were words. */
  /* Arabic port (pA, 7 Oct 2026): TEXT reads the words from EN through T(),
     so a missing attribute or an emptied heading falls back to the page's
     own language. On an English page T() returns EN's text, which is the
     English build's TEXT word for word (tests/ar-port-pA.test.js pins all
     12), so the page is unchanged. */
  var TEXT = {
    deviceTitle: T('sell.device_title'),
    conditionTitle: T('sell.condition_title'),
    batteryTitle: T('sell.battery_title'),
    resultTitle: T('sell.offer_title'),
    paidLine: T('sell.offer_sub'),
    bookLabel: T('sell.book_cta'),
    visitLine: T('sell.payment_note'),
    bridgeHeading: T('sell.bridge_title'),
    browseTitle: T('sell.browse_title'),
    browseText: T('sell.browse_note'),
    browseButton: T('sell.browse_every_device'),
    resetLabel: T('sell.start_again')
  };
  var TEXT_LINES = { paidLine: true, visitLine: true, browseText: true };

  function TX(key) {
    var ds = (root && root.dataset) || {};
    var v = ds['t' + key.charAt(0).toUpperCase() + key.slice(1)];
    if (typeof v !== 'string') return TEXT[key];
    if (/\S/.test(v)) return v;
    return TEXT_LINES[key] ? '' : TEXT[key];
  }

  /* The same words made safe for h(), which writes innerHTML: what she types
     is shown as typed (an "&" or a "<" is text, never markup). Today's words
     carry none of the three characters, so they come out unchanged. */
  function TXH(key) {
    return String(TX(key)).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  var DATA = null;
  // `up` is the device they are considering trading up to, as series|storage.
  // It is a display choice on the result screen only -- it is not an input to
  // quote(), and it never changes the cash figure.
  var S = { brand: null, series: null, storage: null, condition: null, faulty: null, battery: null, up: null, step: 1 };

  var COND_LABELS = {
    flawless: { t: T('sell.cond_flawless_title'), d: T('sell.cond_flawless_desc') },
    good: { t: T('sell.cond_good_title'), d: T('sell.cond_good_desc') },
    fair: { t: T('sell.cond_fair_title'), d: T('sell.cond_fair_desc') },
    damaged: { t: T('sell.cond_damaged_title'), d: T('sell.cond_damaged_desc') }
  };
  /* The four battery answers, 1 Oct 2026: the counter's own battery bands,
     the same keys and words as assets/upgrade-tradein.js. Keys are the
     published offer axis (meta.offer_axes.battery); the September keys
     above90 / 85to90 / below85 are retired and never reused. `unsure` kept
     its key but changed meaning (now the heavy battery amount); this page
     does not restore answers, so no old `unsure` can reach it. Insertion
     order is the button order. */
  /* Arabic phase 3: each label isolated on a right-to-left page, so the range
     "91–95%" keeps its order inside an Arabic button (unisolated it showed
     as "95%–91"). English: isolateText() returns the label untouched. */
  var BATT_LABELS = {
    '96plus': isolateText(T('sell.batt_96plus')),
    '91to95': isolateText(T('sell.batt_91to95')),
    '90orless': isolateText(T('sell.batt_90orless')),
    unsure: isolateText(T('sell.batt_unsure'))
  };

  function egp(n) {
    return new Intl.NumberFormat('en-EG', { maximumFractionDigits: 0 }).format(Math.round(n));
  }

  /* ---- the live sale price ------------------------------------------------
     THE SALE PRICE OF A DEVICE WE SELL COMES FROM SHOPIFY. Ruling, Omar
     Hussein, 22 September 2026. Read from window.UpgradePrices -- the
     server-rendered map of live variant prices mounted once in <head> from
     layout/theme.liquid (snippets/upgrade-price-map.liquid), keyed by full
     variant SKU, <device-code>-{RG|RP|RD}.

     This is the FIFTH surface to move, and it was not in the original four:
     the trade-in bridge on this page prints a target device's price
     (appendTradeInBridge below), computes a trade-up bonus from it and pins
     it into the shared deal state, which the sticky bar then carries to
     checkout. It was still reading variant.list from
     assets/upgrade-tradein-data.json after the catalogue, the product page,
     the homepage rail and the trade-up cards had moved, so it was the last
     place on the storefront where a customer could be shown a sale price the
     checkout would not charge.

     What this file still reads from that JSON is unchanged and is not
     Shopify's: the precomputed offers table and everything meta.offer_*, the
     bonus rate and cap, and device identity. Those are trade-in valuation
     data, not sale prices.

     The bonus formula does not change -- bonusFor(list) is still
     min(round(list x pct), cap). Only the `list` it is handed is now live.

     null means "this site does not know what this costs right now". On this
     surface a null removes the CARD, not just the number: a trade-up card
     states a guaranteed bonus and an estimated balance, and neither can be
     stated against a price we do not know. The route out stays open -- see
     the !ups.length branch of appendTradeInBridge.

     `typeof window` rather than a bare `window`: tests/engines.test.js
     compiles this file with new Function('document', src) and no window at
     all, and a bare reference would throw there rather than read as "no
     price map on this page". */
  function priceApi() {
    return (typeof window !== 'undefined' && window.UpgradePrices) ? window.UpgradePrices : null;
  }

  function livePrice(code, grade) {
    var api = priceApi();
    if (!api || typeof api.getByCode !== 'function' || !code) return null;
    var p = api.getByCode(code, grade);
    return (typeof p === 'number' && isFinite(p) && p > 0) ? p : null;
  }

  /* True when this page cannot state any sale price at all -- no map, or a
     map that rendered with nothing usable in it. Distinguished from "no
     device happens to sit in the trade-up window", because the wording the
     customer should get differs: one is "nothing fits", the other is "we
     cannot price anything right now". */
  function pricesOffline() {
    var api = priceApi();
    return !api || !(typeof api.count === 'number' && api.count > 0);
  }

  function device() {
    if (!S.series) return null;
    for (var i = 0; i < DATA.devices.length; i++) {
      if (DATA.devices[i].series === S.series) return DATA.devices[i];
    }
    return null;
  }

  function variant() {
    var d = device();
    if (!d || !S.storage) return null;
    for (var i = 0; i < d.variants.length; i++) {
      if (d.variants[i].storage === S.storage) return d.variants[i];
    }
    return null;
  }

  /* ---- the offer lookup ---------------------------------------------------
     Ruling of 22 September 2026, and the same rule the Trade-in tool applies
     (see the matching block in assets/upgrade-tradein.js). Every offer this
     page can show was worked out before the data file was built, and only the
     result was published. Nothing here decides an offer, and nothing here can
     be run backwards: the browser holds no acquisition price, no component
     amount and no ladder fraction to run it against.

     Refusal is looked up too, not judged here. It is published twice in the
     row -- a null cash figure and the refusal band letter -- and either one
     alone refuses, so a reader of this code that forgets one still shows no
     number. Why a given combination is refused is not in the file.

     The refusal has to read the same on BOTH tools or the same customer with
     the same handset is refused on one page and given a hard cash number on
     the other -- and the sell page writes that number into the shared deal
     state, so it would also reach the booking summary and the contact email.

     The axes, the stride and the band letters are read from the meta block of
     assets/upgrade-tradein-data.json rather than restated here as literals
     (Ruling R3), the same discipline the bonus rate and cap already follow.
     Missing or malformed means we cannot look a quote up, so we refuse rather
     than publish an unchecked number. */
  function offerIndex() {
    var axes = DATA && DATA.meta && DATA.meta.offer_axes;
    if (!axes || !axes.condition || !axes.battery || !axes.fault) return -1;

    var ci = axes.condition.indexOf(S.condition);
    var bi = axes.battery.indexOf(S.battery);
    var fi = axes.fault.indexOf(!!S.faulty);
    if (ci < 0 || bi < 0 || fi < 0) return -1;

    // meta.offer_index_formula: condition_index * 8 + battery_index * 2 + fi,
    // with the 8 and the 2 derived from the published axes rather than typed.
    // Returns -1, never a guessed index, when an answer is not one the table
    // knows about; the caller reads that as "cannot quote", never as zero.
    return ci * (axes.battery.length * axes.fault.length) + bi * axes.fault.length + fi;
  }

  /* The rung this offer sits on, as the table states it. meta.offer_bands maps
     a band letter to a rung label; a null label means payable but below the
     lowest named rung, and we do not invent a name for it. The refusal code is
     meta.offer_refused_band and is handled in quote(), not named here. Same
     shape, same letters, as bandLabel() in assets/upgrade-tradein.js -- the
     two engines have to describe the same offer the same way. */
  function bandLabel(code) {
    var map = DATA && DATA.meta && DATA.meta.offer_bands;
    if (!map || !Object.prototype.hasOwnProperty.call(map, code)) return null;
    return map[code] || null;
  }

  /* ---- the engine -- identical lookup to the Trade-in tool ---------------
     No arithmetic. One row, one slot, one figure.

     A missing row, an unreadable index or a slot the table marks refused all
     end the same way: refused === true and cash === null, so no figure can
     reach the screen or the shared deal state. Refusal is checked on BOTH
     lanes the data publishes it on -- a null cash value and the refusal band
     letter -- because either one alone would be enough and neither is allowed
     to be the only guard. */
  function quote() {
    var d = device(), v = variant();
    if (!d || !v || !DATA.offers) return null;

    var row = DATA.offers[v.code];
    var i = offerIndex();
    if (!row || !row.c || i < 0 || i >= row.c.length) {
      return { cash: null, band: null, refused: true, variant: v, device: d };
    }

    var cash = row.c[i];
    var code = typeof row.b === 'string' ? row.b.charAt(i) : '';
    var refusedCode = DATA.meta && DATA.meta.offer_refused_band;

    if (cash === null || cash === undefined || code === refusedCode) {
      return { cash: null, band: null, refused: true, variant: v, device: d };
    }

    return {
      cash: cash,
      band: { key: code, label: bandLabel(code) },
      refused: false,
      variant: v,
      device: d
    };
  }

  /* The four answers the offer was looked up against, in the words the
     customer clicked rather than the keys the lookup uses. This is what the
     result panel states instead of a calculation. */
  function batteryPhrase() {
    if (S.battery === 'unsure') return T('sell.priced_batt_unsure');
    var l = BATT_LABELS[S.battery];
    return l ? T('sell.priced_batt', { battery: l.toLowerCase() }) : T('sell.priced_batt_not_stated');
  }

  function pricedSummary(q) {
    var c = COND_LABELS[S.condition];
    return T('sell.priced_summary', {
      phone: q.device.label + ' ' + q.variant.storage,
      condition: (c ? c.t : S.condition),
      battery: batteryPhrase(),
      fault: (S.faulty ? T('sell.priced_fault_yes') : T('sell.priced_fault_no'))
    });
  }

  /* ---- the bridge into trade-in ------------------------------------------
     Selling and trading in are the same valuation with two different endings.
     Sell pays that value in cash. Trade-in ADDS the trade-up bonus to it and
     pays the sum as credit against a device. The customer cannot choose
     between them without seeing both, so both are put on this screen.

     Two components, and only one of them is soft:

       - the valuation of THEIR phone is an estimate, always, because it ends
         in a physical inspection. That line, and only that line, carries the
         "confirmed at inspection" caveat.
       - the trade-up bonus is GUARANTEED. It is a function of the sale price
         of the device being bought and of nothing else -- not the condition,
         battery or age of the old phone. It is known exactly the moment a
         device is picked, so it is never described as an estimate, as
         indicative, or as "up to".

     Which is why this block never states a bonus until a real catalogue
     device is selected: until then the figure does not exist, and a maximum
     printed where an offer belongs is a maximum read as an offer. */

  // The grade every trade-up figure on this page is priced on, so the table,
  // the pinned target and the handoff link can never name three different
  // grades. Same constant, same value, as assets/upgrade-tradein.js.
  var CARD_GRADE = 'p';
  var GRADE_LABELS = { g: 'Gold', p: 'Platinum', d: 'Diamond' };

  /* Ruling R3 -- the rate and the cap live in exactly one place, the meta
     block of assets/upgrade-tradein-data.json, and are not restated here in
     code or in prose. Field preference matches assets/upgrade-deal.js
     (bonus_pct/bonus_cap, falling back to discount_pct/discount_cap), because
     upgrade-deal.js is what computes the sticky bar the customer sees on the
     very next screen after leaving this one. Unreadable means we cannot state
     a bonus, so we state none rather than a guessed one. */
  function bonusRate() {
    var m = DATA && DATA.meta;
    if (!m) return null;
    var pct = (typeof m.bonus_pct === 'number') ? m.bonus_pct : m.discount_pct;
    var cap = (typeof m.bonus_cap === 'number') ? m.bonus_cap : m.discount_cap;
    if (typeof pct !== 'number' || !isFinite(pct) || pct <= 0 || pct > 1) return null;
    if (typeof cap !== 'number' || !isFinite(cap) || cap <= 0) return null;
    return { pct: pct, cap: cap };
  }

  /* The bonus for one real device at its real list price.

     SINGLE IMPLEMENTATION, 29 Sep 2026 (LOG-029). Delegates to
     window.UpgradeDeal.bonusFor() -- the one place the offer-aware rate
     resolution lives -- exactly as assets/upgrade-tradein.js now does; see
     that file's own comment on this function for the full reasoning. The
     local bonusRate() computation just above remains the fallback for when
     UpgradeDeal is not on the page (this file's own isolated test harness
     only); it does not apply an offer rate. */
  function cardSku(code) {
    if (!code) return null;
    var api = priceApi();
    if (!api || typeof api.skuFor !== 'function') return null;
    try { return api.skuFor(code, CARD_GRADE) || null; } catch (e) { return null; }
  }

  function bonusFor(listPrice, code) {
    // 29 Sep 2026, review fix (should-fix, finding 20): the price guard used
    // to run only on the FALLBACK path below. window.UpgradeDeal.bonusFor()
    // does `(price || 0) * r.pct` internally -- correct for a real device
    // price, but it turns an unusable price into a real number rather than
    // null: NaN/undefined/null all became "0 EGP, guaranteed" instead of no
    // bonus shown at all, and a negative price produced a negative bonus.
    // Both broke this file's own contract ("Returns null ... never zero").
    if (typeof listPrice !== 'number' || !isFinite(listPrice) || listPrice <= 0) return null;
    var dealApi = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
    if (dealApi && typeof dealApi.bonusFor === 'function') {
      return dealApi.bonusFor(listPrice, cardSku(code));
    }
    var r = bonusRate();
    if (!r) return null;
    return Math.min(Math.round(listPrice * r.pct), r.cap);
  }

  /* Devices worth trading up to: same brand, priced above what they are
     handing in. Deliberately the same filter, the same window and the same
     three-result cap as tradeUpOptions() in assets/upgrade-tradein.js, so the
     two pages offer the same customer the same devices. */
  /* THE GENERATION OF A DEVICE, read off its own series code.
     Mirror of the same pair in assets/upgrade-tradein.js -- see that file for
     the full reasoning, including why `>` and not `>=`. In short: a trade-up
     goes UP IN GENERATIONS, strictly. Selling a 14 offers a 15, a 15 Pro Max or
     a 16; never a 13, and never another 14 in any tier. When nothing newer is
     in range the rail shows nothing and the customer browses instead. The
     number is the first run of digits in the catalogue's own series code; all
     27 devices parse. No cross-brand comparison is attempted -- the caller has
     already required the same brand. Omar, 24 Sep 2026. */
  function generationOf(series) {
    var m = /(\d+)/.exec(series || '');
    return m ? parseInt(m[1], 10) : null;
  }

  function isNewerGeneration(targetSeries, sourceSeries) {
    var t = generationOf(targetSeries);
    var s = generationOf(sourceSeries);
    if (t === null || s === null) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[upgrade-sell] cannot read a generation from "' +
          (t === null ? targetSeries : sourceSeries) + '", so it is not offered as a trade-up. ' +
          'Series codes must carry their generation as digits (IPHONE15PROMAX, S24ULTRA).');
      }
      return false;
    }
    return t > s;
  }

  /* ---- the model list's order (Lynkrs Q7 / D3, batch 2, W1b, 6 Oct 2026) --
     Newest first, grouped by family: the rule batch 2 set for the trade-in
     tool (assets/upgrade-tradein.js) and the shop too. Apple, then Samsung;
     within a brand the generation descending (iPhone 17 > 16 > 15 > 14 > 13;
     Galaxy S25 > S24 > S23); within a generation the top model first (Ultra
     or Pro Max, then Pro, then Plus, then the base model). Read from each
     device's own brand and series code, so a phone added to the data file
     takes its place without an edit here; the file itself is never
     reordered (run_all.py checksums it). The buttons used to follow the
     file: iPhone 13, 14, 15, 16, 15 Pro Max, 13 Pro... Display only: the
     offer, the bonus and the trade-up cards do not depend on it. */
  var BRAND_ORDER = ['Apple', 'Samsung'];

  function tierOf(series) {
    var rest = String(series || '').toUpperCase().replace(/^[A-Z]*\d+/, '');
    if (rest === 'ULTRA' || rest === 'PROMAX') return 0;
    if (rest === 'PRO') return 1;
    if (rest === 'PLUS') return 2;
    if (rest === '') return 3;
    return 4;   // a model name this list does not know goes after the known ones
  }

  function modelOrder(a, b) {
    var ba = BRAND_ORDER.indexOf(a.brand), bb = BRAND_ORDER.indexOf(b.brand);
    if (ba === -1) ba = BRAND_ORDER.length;
    if (bb === -1) bb = BRAND_ORDER.length;
    if (ba !== bb) return ba - bb;
    var ga = generationOf(a.series), gb = generationOf(b.series);
    if (ga !== gb) return (gb === null ? -1 : gb) - (ga === null ? -1 : ga);
    return tierOf(a.series) - tierOf(b.series);
  }

  // Stable in every browser: two devices the rule cannot tell apart keep the file's order.
  function sortModels(list) {
    return list.map(function (d, i) { return { d: d, i: i }; })
      .sort(function (x, y) { return modelOrder(x.d, y.d) || (x.i - y.i); })
      .map(function (x) { return x.d; });
  }

  /* TRADE-UP IN STOCK, 11 Oct 2026 (2026-10-11_tradeup-in-stock/CHANGES.md).
     The same rule, the same source and the same fail-closed reading as
     cardInStock() in assets/upgrade-tradein.js: a device is offered to trade
     up to only when Shopify says the exact phone is in stock (this series and
     storage, CARD_GRADE, any colour; window.UpgradeFacts.inStock). Unknown is
     NOT in stock, said once in the console. With nothing in stock the bridge
     takes its existing no-device path (heading, then the way to the shop).
     Nothing here reads or changes a price, a bonus or a balance. */
  var stockWarned = false;
  // The grade key's suffix, as window.UpgradePrices.SUFFIX names it: <CODE>-<RX> is the key the facts are kept by.
  var STOCK_SUFFIX = { g: 'RG', p: 'RP', d: 'RD' };

  function cardInStock(code) {
    var facts = (typeof window !== 'undefined') ? window.UpgradeFacts : null;
    var sku = code ? String(code).trim().toUpperCase() + '-' + STOCK_SUFFIX[CARD_GRADE] : null;
    var known = null;
    if (sku && facts && typeof facts.inStock === 'function') {
      try { known = facts.inStock(sku); } catch (e) { known = null; }
    }
    if (known === true) return true;
    if (known !== false && !stockWarned && typeof console !== 'undefined' && typeof console.warn === 'function') {
      stockWarned = true;
      console.warn('[upgrade-sell] no stock fact for ' + (sku || code) + ' (window.UpgradeFacts.inStock), so it is ' +
        'not offered as a trade-up: a device is offered only when Shopify says it is in stock.');
    }
    return false;
  }

  function tradeUpOptions(q) {
    var out = [];
    DATA.devices.forEach(function (d) {
      if (d.brand !== q.device.brand) return;
      if (d.series === q.device.series) return; // never offer the device they are handing in
      // Up in generations, strictly -- see isNewerGeneration above.
      if (!isNewerGeneration(d.series, q.device.series)) return;
      d.variants.forEach(function (v) {
        /* The card's price, its bonus and its estimated balance are all
           computed from this one figure, so it is the LIVE Platinum price of
           this exact variant, not the static copy this file used to read.
           A variant Shopify has no price for is not offered as a card. */
        var list = livePrice(v.code, CARD_GRADE);
        if (list === null) return;

        /* Margin measured against the CREDIT, not the phone. See the matching
           note in assets/upgrade-tradein.js — the two rails share this rule and
           had the same defect. Fixed 23 September 2026: the 5% headroom was
           being left over q.cash alone, then the bonus (up to 4,000 EGP) was
           added on top, so the pre-selected card routinely carried a credit
           larger than the device's own price and printed "Estimated balance to
           pay 0". It is worse here than on the trade-in page, because
           selectedUp() defaults to ups[0] — the CHEAPEST qualifying target,
           which is exactly the one most likely to sit under the credit. The
           zero was the default, not an edge. */
        var bonus = bonusFor(list, v.code);
        if (bonus === null) return;
        if (list > (q.cash + bonus) * 1.05 && list < q.cash * 2.6) {
          // TRADE-UP IN STOCK, 11 Oct 2026: only a phone in stock, left out before the sort and the cut (cardInStock above).
          if (!cardInStock(v.code)) return;
          out.push({
            label: d.label, storage: v.storage, list: list,
            series: d.series, code: v.code
          });
        }
      });
    });
    out.sort(function (a, b) { return a.list - b.list; });
    var seen = {}, uniq = [];
    for (var i = 0; i < out.length && uniq.length < 3; i++) {
      if (seen[out[i].label]) continue;
      seen[out[i].label] = 1; uniq.push(out[i]);
    }
    return uniq;
  }

  function upKey(u) { return u.series + '|' + u.storage; }

  function selectedUp(ups) {
    for (var i = 0; i < ups.length; i++) {
      if (S.up === upKey(ups[i])) return ups[i];
    }
    return ups[0];
  }

  /* The handoff. What the customer told us about THEIR phone is already in
     the shared deal state -- setTradein() ran a few lines above, in
     stepResult() -- so it travels with them and nothing is asked twice.

     This pins the phone they are trading up TO, exactly as the trade-up cards
     in assets/upgrade-tradein.js do, and sends them to that device's page.
     From there the sticky bar in assets/upgrade-deal.js carries both halves
     of the deal and ends in the same booking step.

     Note what is NOT emitted here: the customer's device, condition or
     battery as URL parameters. assets/upgrade-tradein.js reads exactly three
     parameters -- target, targetStorage, targetGrade -- all of them about the
     device being BOUGHT. It reads nothing at all about the phone being handed
     in, and it does not restore its own four answers from the shared state
     either: it always starts at step 1. So routing to /pages/trade-in would
     make them answer the same four questions a second time, which is the one
     thing this handoff exists to prevent. The device page is the route that
     keeps what they have already told us. */
  // Lynkrs B6 (6 Oct 2026, T3): the phone's own product page,
  // /products/<handle>, from window.UpgradeRoutes, with the storage and grade
  // this card was priced on (the shop if Shopify has no product for it). It
  // used to be /pages/device?device=<series>.
  function deviceHref(u) {
    // Arabic port (pA): the address keeps the language prefix (localHref()).
    var routes = window.UpgradeRoutes;
    return (routes && typeof routes.deviceHref === 'function')
      ? localHref(routes.deviceHref(u.series, { grade: CARD_GRADE, storage: u.storage }))
      : routeRoot() + 'pages/shop';
  }

  function pinTradeUpTarget(u) {
    if (!window.UpgradeDeal) return;
    var dev = null, v = null;
    DATA.devices.forEach(function (d) { if (d.series === u.series) dev = d; });
    if (dev) dev.variants.forEach(function (x) { if (x.storage === u.storage) v = x; });
    var base = root.dataset.assetBase || '';
    window.UpgradeDeal.setTarget({
      series: u.series,
      label: u.label,
      image: (dev && dev.image_cut && base) ? (base + dev.image_cut) : (dev ? dev.image : null),
      grade: CARD_GRADE,
      gradeLabel: GRADE_LABELS[CARD_GRADE],
      storage: u.storage,
      code: u.code || (v ? v.code : null),
      /* The live Platinum price this card was built on. tradeUpOptions() has
         already proved it is a real number -- a card is never built without
         one -- so nothing null can reach UpgradeDeal.totals(), which reads
         `target.price || 0` and would otherwise publish a 0 EGP phone, a 0
         bonus and a 0 balance to the sticky bar. */
      price: u.list
    });
  }

  /* 2 Oct 2026 (Omar): testers missed this as a plain text link. Same
     highlighted block as assets/upgrade-tradein.js (.ug-browse, upgrade-core.css). */
  function browseNote() {
    var box = h('div', 'ug-browse');
    box.appendChild(h('p', 'ug-browse__k', TXH('browseTitle')));
    var browseText = TXH('browseText');   // Lynkrs batch 2 (W1b): her words; emptied, left out
    if (browseText) box.appendChild(h('p', 'ug-browse__note', browseText));
    var go = h('a', 'ug-btn ug-btn--primary ug-browse__btn', TXH('browseButton'));
    go.href = routeRoot() + 'pages/shop';
    box.appendChild(go);
    return box;
  }

  /* Appends the whole trade-in alternative to the result body. Called only on
     a quote the offer table priced; the refusal path never reaches it. */
  function appendTradeInBridge(body, q, rate, ups) {
    body.appendChild(h('p', 'ug-sell-offer__k', TXH('bridgeHeading')));

    if (!rate) {
      // No published rate means no bonus we can stand behind. The route stays
      // open; the number does not appear.
      body.appendChild(h('p', 'ug-sell-note', T('sell.bridge_no_rate', { device: q.device.label })));
      body.appendChild(browseNote());
      return;
    }

    if (!ups.length) {
      /* Two different reasons land here and they are not told apart in the
         wording on purpose -- both end in "no figure, route still open".
         Either nothing in the catalogue sits in the trade-up window for this
         phone, or the live price map is dark and this page cannot state any
         sale price at all. In both cases there is no device we can price a
         bonus against, so no bonus is named. What is never done is fall back
         to the static figure this file used to read.

         The price map already console.errors its own cause; this names the
         consequence on this surface, which is the one a monitor would miss:
         the trade-up bridge quietly loses its cards. */
      if (pricesOffline() && typeof console !== 'undefined' && typeof console.warn === 'function') {
        var api = priceApi();
        console.warn('[upgrade-sell] no trade-up card can be priced: window.UpgradePrices state=' +
          (api ? api.state : 'absent') + ' count=' + (api ? api.count : 0) +
          '. The trade-in route stays open with no figure.');
      }
      body.appendChild(browseNote());
      return;
    }

    var u = selectedUp(ups);

    /* 29 Sep 2026, review fix (should-fix, findings 5/13). MOVED here, below
       the device pick, and now reads window.UpgradeDeal.rateFor(sku) for the
       CHOSEN device -- not the static `rate` param (bonusRate(), the
       standing rate only) this sentence used to quote unconditionally. Before
       this fix, a live promotion with its own bonus rate (LOG-029) could
       show "The bonus is 10% ... capped at 4,000" in this sentence directly
       above a table computed at the promotion's real 5% / 2,500 -- two
       numbers on one screen disagreeing about the same guaranteed figure.
       rateFor() falls back to the standing rate itself when no offer rate
       applies, so this reads correctly whether or not a promotion is live.
       `rate` (the caller's bonusRate()) remains the fallback for when
       UpgradeDeal is not on the page (this file's own isolated test harness
       only), matching bonusFor()'s own fallback above. */
    var stated = (typeof window !== 'undefined' && window.UpgradeDeal && typeof window.UpgradeDeal.rateFor === 'function')
      ? (window.UpgradeDeal.rateFor(cardSku(u.code)) || rate)
      : rate;
    body.appendChild(h('p', 'ug-sell-note', T('sell.bridge_explainer', {
      pct: pctFig(stated.pct), cap: fig(stated.cap), device: q.device.label
    })));

    if (ups.length > 1) {
      body.appendChild(h('p', 'ug-sell-note', T('sell.bridge_pick')));
      var chips = h('div', 'ug-sell-choices ug-sell-choices--wrap', null);
      chips.setAttribute('role', 'radiogroup');
      chips.setAttribute('aria-label', TP('sell.bridge_pick_label'));
      ups.forEach(function (opt) {
        var isSel = upKey(opt) === upKey(u);
        var btn = h('button', 'ug-storage-chip' + (isSel ? ' is-selected' : ''), opt.label + ' ' + opt.storage);
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', isSel ? 'true' : 'false');
        btn.onclick = function () { S.up = upKey(opt); render(); };
        chips.appendChild(btn);
      });
      body.appendChild(chips);
    }

    var b = bonusFor(u.list, u.code);
    var credit = q.cash + b;
    /* Unclamped on purpose — see assets/upgrade-tradein.js. tradeUpOptions()
       now excludes any target the credit already covers, so this cannot go
       negative; if it ever does again the bridge is withdrawn and the console
       says why, rather than the customer reading "balance to pay 0". */
    var payable = u.list - credit;
    if (!(payable > 0)) {
      /* Same shape as the !ups.length path above: no figure, route still open.
         Returning bare would leave the heading and the device chips standing
         over nothing, so browseNote() goes in first — the customer keeps a way
         forward and simply is not shown a balance we cannot state honestly. */
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[upgrade-sell] suppressed the trade-up bridge: credit ' + credit +
          ' covers ' + u.label + ' ' + u.storage + ' at ' + u.list +
          ' (balance ' + payable + '). The band in tradeUpOptions() should have excluded this.');
      }
      body.appendChild(browseNote());
      return;
    }

    /* The two components stay on two lines. Netting them into one "you pay"
       figure is what the previous build did, and it hid the only part of the
       deal that is fixed. */
    var tbl = h('table', 'ug-sell-table');
    tbl.innerHTML = '<tbody>' +
      '<tr><th>' + T('sell.bridge_row_value', { phone: q.device.label + ' ' + q.variant.storage }) + '</th><td>' + fig(q.cash) + '</td></tr>' +
      '<tr><th>' + T('sell.bridge_row_bonus') + '</th><td>' + plusFig(b) + '</td></tr>' +
      '<tr class="ug-sell-table__tot"><th>' + T('sell.bridge_row_credit') + '</th><td>' + fig(credit) + '</td></tr>' +
      '<tr><th>' + T('sell.bridge_row_target', { device: u.label + ' ' + u.storage, grade: gradeName(CARD_GRADE) }) + '</th><td>' + fig(u.list) + '</td></tr>' +
      '<tr class="ug-sell-table__tot"><th>' + T('sell.bridge_row_balance') + '</th><td>' + fig(payable) + '</td></tr>' +
      '</tbody>';
    body.appendChild(tbl);

    // 28 Sep 2026 (LOG-021): a trade-in ends in an order, checked at the door.
    body.appendChild(h('p', 'ug-sell-note', T('sell.bridge_note', {
      bonus: fig(b), target: u.label + ' ' + u.storage, target_model: u.label, phone: q.device.label
    })));

    /* Secondary, deliberately. The cash offer above keeps the primary button:
       selling outright is a complete choice, not a detour. */
    var upRow = h('div', 'ug-sell-cta-row');
    var upBtn = h('a', 'ug-btn ug-btn--secondary ug-btn--full', T('sell.bridge_cta', { device: u.label + ' ' + u.storage }));
    upBtn.href = deviceHref(u);
    upBtn.addEventListener('click', function () { pinTradeUpTarget(u); });
    upRow.appendChild(upBtn);
    body.appendChild(upRow);

    body.appendChild(browseNote());
  }

  /* ---- rendering ---------------------------------------------------------*/
  function h(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  /* Every trade-up figure on this screen is derived HERE, on each call:
     stepResult() re-runs quote() and tradeUpOptions(), and tradeUpOptions()
     re-asks window.UpgradePrices for every candidate variant. So a second
     render against a fuller price map does not just redraw the cards it drew
     last time -- it re-decides which cards exist. That is the property the
     repaint at the foot of this file depends on, because an unpriced trade-up
     target is REMOVED here rather than shown as a hotline card: an early read
     leaves no placeholder on screen for a customer to interpret, just fewer
     options than we actually have.

     `opts.silent` suppresses the scroll-into-view. A render the customer
     caused should carry them to the step they just answered; a repaint they
     did not ask for -- prices landing after the first paint -- must not move
     the page under them mid-read. */
  function render(opts) {
    var hadFocus = focusKey();   // Q21: the answer that had focus, if any
    root.innerHTML = '';
    root.appendChild(stepCount());
    root.appendChild(steps());

    if (S.step >= 1) root.appendChild(stepDevice());
    if (S.step >= 2) root.appendChild(stepCondition());
    if (S.step >= 3) root.appendChild(stepBattery());
    if (S.step >= 4) root.appendChild(stepResult());

    restoreFocus(hadFocus);

    // Lynkrs Q21 (6 Oct 2026, T2): was an unconditional smooth scroll to the
    // middle of the window on every answer -- see reveal().
    var active = root.querySelector('.ug-sell-step--active');
    if (active && S.step > 1 && !(opts && opts.silent)) {
      reveal(active);
    }
  }

  /* ---- moving the page for the customer (Lynkrs T2, 6 Oct 2026) ----------
     Q21, the same rule as assets/upgrade-tradein.js. Every answer used to
     smooth-scroll the active step to the MIDDLE of the window, whether or not
     it was already in view; on a phone a tap that lands while the page is
     still gliding can hit whatever has slid under the finger, or only stop
     the glide (the likely cause of "the first tap only scrolls"; not
     reproduced in headless Chrome, 6 Oct 2026). Now the page moves only when the
     step is NOT fully in view between the sticky header and the deal bar, it
     moves as little as it can (a step taller than the window is brought to
     its top, where "center" left the "Your cash offer" heading about 570px
     above the screen at 375px wide, measured offline on 6 Oct 2026),
     and it jumps instead of gliding under reduced motion. */
  function reducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }
  }

  /* Below the sticky header (the step's own scroll-margin-top, set in
     upgrade-sell.css) and above the deal bar (--ug-deal-h on <html>). */
  function visibleBand(el) {
    var band = { top: 0, bottom: window.innerHeight || 0 };
    try {
      band.top = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
      band.bottom -= parseFloat(document.documentElement.style.getPropertyValue('--ug-deal-h')) || 0;
    } catch (e) { /* no layout here: the whole window */ }
    return band;
  }

  function fullyInView(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function' || !window.innerHeight) return false;
    var r = el.getBoundingClientRect();
    var band = visibleBand(el);
    return r.top >= band.top && r.bottom <= band.bottom;
  }

  /* true when it moved the page. */
  function reveal(el) {
    if (!el || typeof el.scrollIntoView !== 'function' || fullyInView(el)) return false;
    var band = visibleBand(el);
    var tall = el.getBoundingClientRect().height > (band.bottom - band.top);
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: tall ? 'start' : 'nearest' });
    return true;
  }

  /* Q21, the other half: render() rebuilds the whole tool, so the answer the
     customer had just pressed was removed and focus fell to <body> (measured
     in Chrome, 6 Oct 2026) -- the radio state (role="radio", aria-checked) of
     the answer just given was never announced. Same fix as the trade-in tool:
     note the focused answer (class, words, step) before the rebuild and focus
     the same answer after it, without scrolling. */
  function focusKey() {
    var a = document.activeElement;
    if (!a || a.tagName !== 'BUTTON' || typeof root.contains !== 'function' || !root.contains(a)) return null;
    var steps = root.querySelectorAll('.ug-sell-step');
    var step = a.closest ? a.closest('.ug-sell-step') : null;
    return { cls: String(a.className).split(' ')[0], text: a.textContent, step: step ? Array.prototype.indexOf.call(steps, step) : -1 };
  }

  function restoreFocus(k) {
    if (!k || !k.cls) return;
    var scope = k.step >= 0 ? root.querySelectorAll('.ug-sell-step')[k.step] : root;
    if (!scope) return;
    var list = scope.querySelectorAll('button.' + k.cls);
    for (var i = 0; i < list.length; i++) {
      if (list[i].textContent === k.text) {
        try { list[i].focus({ preventScroll: true }); } catch (e) { /* nothing to restore to */ }
        return;
      }
    }
  }

  /* Lynkrs Q6 / D1 (batch 2, W1b, 6 Oct 2026): the strip says where the
     customer is, "Step 2 of 4", with M read from the strip itself, so the
     count cannot drift from the stations drawn under it. The page copy no
     longer promises "three questions" (templates/page.sell.json: "a few
     quick questions"). The words carry two numbers, so they stay here
     (Arabic rule R4: a string with a placeholder is a key, not a setting). */
  // Arabic port (pA): the four station names are the EN sell.step_* keys
  // (the strip's words before batch 2), and the count is sell.step_count.
  var STEP_LABELS = [T('sell.step_device'), T('sell.step_condition'), T('sell.step_battery'), T('sell.step_offer')];

  function stepCount() {
    var n = Math.min(Math.max(S.step, 1), STEP_LABELS.length);
    return h('p', 'ug-sell-progress__count', T('sell.step_count', { n: n, total: STEP_LABELS.length }));
  }

  function steps() {
    var wrap = h('ol', 'ug-sell-progress');
    STEP_LABELS.forEach(function (t, i) {
      var li = h('li', 'ug-sell-progress__item' + (S.step > i ? ' is-done' : '') + (S.step === i + 1 ? ' is-current' : ''));
      li.appendChild(h('span', 'ug-sell-progress__num', String(i + 1)));
      li.appendChild(h('span', 'ug-sell-progress__label', t));
      wrap.appendChild(li);
    });
    return wrap;
  }

  function card(title, n, active) {
    var s = h('section', 'ug-sell-step' + (active ? ' ug-sell-step--active' : ''));
    var head = h('div', 'ug-sell-step__head');
    head.appendChild(h('span', 'ug-sell-step__n', String(n)));
    head.appendChild(h('h3', 'ug-sell-step__title', title));
    s.appendChild(head);
    return s;
  }

  function stepDevice() {
    var s = card(TXH('deviceTitle'), 1, S.step === 1);
    var body = h('div', 'ug-sell-step__body');

    /* Launch punch list S9 (3), 27 Sep 2026. A new brand or model means the
       valuation in the shared state no longer describes the phone on screen,
       so it goes with the answers -- otherwise the booking form below goes on
       showing, and emailing, the offer for the phone they moved away from.
       Same fix as assets/upgrade-tradein.js. */
    var brands = h('div', 'ug-sell-choices', null);
    brands.setAttribute('role', 'radiogroup');
    brands.setAttribute('aria-label', TP('sell.brand_label'));
    ['Apple', 'Samsung'].forEach(function (b) {
      var btn = h('button', 'ug-storage-chip' + (S.brand === b ? ' is-selected' : ''), b);
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', S.brand === b ? 'true' : 'false');
      btn.onclick = function () {
        S.brand = b; S.series = null; S.storage = null; S.step = 1;
        if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
        render();
      };
      brands.appendChild(btn);
    });
    body.appendChild(brands);

    if (S.brand) {
      // Lynkrs Q7 (W1b): newest first, top model first -- sortModels() above.
      var list = sortModels(DATA.devices.filter(function (d) { return d.brand === S.brand && d.tradein; }));
      var grid = h('div', 'ug-sell-choices ug-sell-choices--wrap', null);
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', TP('sell.model_label'));
      list.forEach(function (d) {
        var btn = h('button', 'ug-storage-chip' + (S.series === d.series ? ' is-selected' : ''), d.label);
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', S.series === d.series ? 'true' : 'false');
        btn.onclick = function () {
          S.series = d.series; S.storage = null;
          if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
          render();
        };
        grid.appendChild(btn);
      });
      body.appendChild(grid);

      var others = DATA.devices.filter(function (d) { return d.brand === S.brand && !d.tradein; }).length;
      if (others) {
        var otherContact = buildContact("Hi, I have a phone to sell that's not listed on the site.");
        var otherLinkHtml = otherContact.isWhatsapp
          ? '<a href="' + otherContact.href + '" class="ug-sell-link" target="_blank" rel="noopener" title="' + T('sell.whatsapp_demo_title') + '">' + T('sell.whatsapp_link_demo') + '</a>'
          : '<a href="' + otherContact.href + '" class="ug-sell-link">' + T('sell.call_our_hotline', { hotline: HOTLINE }) + '</a>';
        body.appendChild(h('p', 'ug-sell-note', T('sell.another_model', { link: otherLinkHtml })));
      }
    }

    if (S.series) {
      var d2 = device();
      var pills = h('div', 'ug-sell-choices', null);
      pills.setAttribute('role', 'radiogroup');
      pills.setAttribute('aria-label', TP('sell.storage_label'));
      /* Launch punch list S9 (2), 27 Sep 2026. A storage the data file
         withdraws from quoting (tradein: false, e.g. iPhone 16 512GB) has no
         offers row, so picking it ended in the refusal copy -- "falls below
         the lowest offer we will quote" -- which is not why it has no price.
         It is not offered at all, the rule the model list above applies to
         whole devices. Same filter as assets/upgrade-tradein.js. */
      d2.variants.filter(function (v) { return v.tradein !== false; }).forEach(function (v) {
        var btn = h('button', 'ug-storage-chip' + (S.storage === v.storage ? ' is-selected' : ''), v.storage);
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', S.storage === v.storage ? 'true' : 'false');
        btn.onclick = function () { S.storage = v.storage; S.step = Math.max(S.step, 2); render(); };
        pills.appendChild(btn);
      });
      body.appendChild(pills);
    }

    s.appendChild(body);
    return s;
  }

  function stepCondition() {
    var s = card(TXH('conditionTitle'), 2, S.step === 2);
    var body = h('div', 'ug-sell-step__body');
    var grid = h('div', 'ug-sell-conditions', null);
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', TP('sell.condition_label'));
    Object.keys(COND_LABELS).forEach(function (k) {
      var c = COND_LABELS[k];
      var btn = h('button', 'ug-sell-condition' + (S.condition === k ? ' is-selected' : ''));
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', S.condition === k ? 'true' : 'false');
      btn.appendChild(h('span', 'ug-sell-condition__t', c.t));
      btn.appendChild(h('span', 'ug-sell-condition__d', c.d));
      btn.onclick = function () { S.condition = k; S.step = Math.max(S.step, 2); render(); };
      grid.appendChild(btn);
    });
    body.appendChild(grid);

    if (S.condition) {
      body.appendChild(h('p', 'ug-sell-note', T('sell.fault_question')));
      var faultRow = h('div', 'ug-sell-choices', null);
      faultRow.setAttribute('role', 'radiogroup');
      faultRow.setAttribute('aria-label', TP('sell.fault_label'));
      [{ k: 'no', t: T('sell.fault_no') }, { k: 'yes', t: T('sell.fault_yes') }].forEach(function (opt) {
        var isSel = S.faulty !== null && (opt.k === 'yes') === S.faulty;
        var btn = h('button', 'ug-storage-chip' + (isSel ? ' is-selected' : ''), opt.t);
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', isSel ? 'true' : 'false');
        btn.onclick = function () { S.faulty = (opt.k === 'yes'); S.step = Math.max(S.step, 3); render(); };
        faultRow.appendChild(btn);
      });
      body.appendChild(faultRow);
    }

    s.appendChild(body);
    return s;
  }

  /* Where the customer finds the figure (1 Oct 2026). Apple support 120745
     (17 Sep 2026): the iPhone 15 and later show Settings > Battery > Battery
     Health; the iPhone 14 and earlier show Battery Health & Charging. Samsung
     shows Normal / Weak / Bad and no percentage (Samsung Egypt, 3 Dec 2025),
     so a Samsung owner is told to press Don't know. Same words as
     assets/upgrade-tradein.js. */
  function batteryHelp() {
    if (S.brand !== 'Apple') return T('sell.battery_help_samsung');
    var g = generationOf(S.series);
    return (g !== null && g >= 15)
      ? T('sell.battery_help_iphone15')
      : T('sell.battery_help_iphone14');
  }

  function stepBattery() {
    var s = card(TXH('batteryTitle'), 3, S.step === 3);
    var body = h('div', 'ug-sell-step__body');
    body.appendChild(h('p', 'ug-sell-note', batteryHelp()));
    var pills = h('div', 'ug-sell-choices', null);
    pills.setAttribute('role', 'radiogroup');
    pills.setAttribute('aria-label', TP('sell.battery_label'));
    Object.keys(BATT_LABELS).forEach(function (k) {
      var btn = h('button', 'ug-storage-chip' + (S.battery === k ? ' is-selected' : ''), BATT_LABELS[k]);
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', S.battery === k ? 'true' : 'false');
      btn.onclick = function () { S.battery = k; S.step = 4; render(); };
      pills.appendChild(btn);
    });
    body.appendChild(pills);
    s.appendChild(body);
    return s;
  }

  /* The whole quote, written out as one sentence a human can read.
     buildContact() only uses it on the WhatsApp branch, and every
     whatsapp_number setting in this theme is deliberately an empty string
     until a verified Upgrade WhatsApp Business number exists -- so in the
     shipped store this text is built, handed over and dropped in favour of
     the tel: hotline. It is kept here, correct and reachable, so that the
     day a number is configured the message goes out complete. Nothing else
     is wired to it.
     Arabic phase 3: a message TO STAFF, so it stays English in every
     language. COND_LABELS / BATT_LABELS now carry the page's language, so
     the condition and battery words are read from the ENGLISH table (EN),
     the staff copy -- on an English page the same words as before.
     tools/build-i18n.js refuses a translation call inside this function. */
  function sellEnquiryMessage(q) {
    return 'Hi, I would like to sell my ' + q.device.label + ' ' + q.variant.storage + ' to Upgrade. ' +
      'Condition: ' + EN['sell.cond_' + S.condition + '_title'] + '. Functional fault: ' + (S.faulty ? 'Yes' : 'No') + '. Battery: ' + EN['sell.batt_' + S.battery] + '. ' +
      'My calculated offer is ' + egp(q.cash) + ' EGP – could you confirm a collection or drop-off time?';
  }

  function stepResult() {
    var q = quote();
    var s = card(TXH('resultTitle'), 4, true);
    var body = h('div', 'ug-sell-step__body');
    if (!q) { body.appendChild(h('p', 'ug-sell-note', T('sell.select_storage'))); s.appendChild(body); return s; }

    /* The refusal side -- the same refusal the Trade-in tool applies, for the
       same reason. The published outcome for this combination carries no
       figure (or the row itself is unreadable, which is the same thing: we
       have no number we can stand behind). No figure goes on screen and
       nothing is published into the shared state, so a stale value from a
       previous answer cannot survive into the deal bar, the booking summary
       or the contact email. A buyer prices it by hand instead. */
    if (q.refused) {
      if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();

      body.appendChild(h('p', 'ug-sell-offer__k', T('sell.refused_title')));
      /* The refusal is the table's (band X); this only says why, from the
         customer's own answers. Since 1 Oct 2026 a declared fault is never
         priced online, so the floor sentence would give it a false reason.
         Same words as assets/upgrade-tradein.js. */
      body.appendChild(h('p', 'ug-sell-note',
        T(S.faulty ? 'sell.refused_fault' : 'sell.refused_floor', { phone: q.device.label + ' ' + q.variant.storage })));

      var refusedContact = buildContact('Hi, I would like to sell my ' + q.device.label + ' ' +
        q.variant.storage + '. The website asked me to speak to someone about it.');
      var callBtn = h('a', 'ug-btn ug-btn--primary ug-btn--full',
        refusedContact.isWhatsapp ? T('sell.whatsapp_link_demo') : T('sell.call_our_hotline', { hotline: HOTLINE }));
      callBtn.href = refusedContact.href;
      if (refusedContact.isWhatsapp) {
        callBtn.target = '_blank';
        callBtn.rel = 'noopener';
        callBtn.title = T('sell.whatsapp_demo_title');
      }
      body.appendChild(callBtn);

      body.appendChild(resetBtn());
      s.appendChild(body);
      return s;
    }

    // Publish the valuation into the shared deal state. This is what closes
    // the loop in the other direction: value your phone here, then browse,
    // and the sticky bar shows what you'd pay the moment you pick a device.
    /* Launch punch list S9 (1), 27 Sep 2026. This page is the cash sale, so
       any phone pinned earlier as the target is let go first: the product
       page pins one on every view, and the booking form below read "target +
       valuation" as a purchase with trade-in and emailed a cash sale that
       way. Nothing is lost -- the trade-up button in the bridge below pins its
       own target on click, and any product page pins one again.
       S9 (4), same date: the fault answer travels with the valuation (the
       same !! the lookup uses), so the booking email can state it. */
    if (window.UpgradeDeal) {
      window.UpgradeDeal.clearTarget();
      window.UpgradeDeal.setTradein({
        series: q.device.series, label: q.device.label, storage: q.variant.storage,
        condition: S.condition, battery: S.battery, faulty: !!S.faulty, value: q.cash
      });
    }

    // The trade-in alternative, worked out before anything is drawn: the line
    // under the cash offer must not promise a comparison the bridge below
    // then cannot show.
    var bonusRates = bonusRate();
    var ups = bonusRates ? tradeUpOptions(q) : [];

    var offer = h('div', 'ug-sell-offer');
    offer.appendChild(h('span', 'ug-sell-offer__k', T('sell.offer_label', { phone: q.device.label + ' ' + q.variant.storage })));
    offer.appendChild(h('strong', 'ug-sell-offer__v',
      '<span class="ug-price ug-price--lg">' + fig(q.cash) + ' <span class="ug-price__ccy">' + T('common.currency') + '</span></span>'));
    var paidLine = TXH('paidLine');   // Lynkrs batch 2 (W1b): a setting; emptied, left out
    if (paidLine) offer.appendChild(h('span', 'ug-sell-offer__s', paidLine));
    body.appendChild(offer);

    // The choice is live at this number, so it is named at this number. The
    // figures themselves sit in the bridge further down, where a device has
    // been chosen and the bonus is an exact amount rather than a range.
    if (bonusRates && ups.length) {
      body.appendChild(h('p', 'ug-sell-note', T('sell.offer_compare_note')));
    }

    /* What we priced -- this is what makes the offer credible rather than a
       magic number, and it is the only form of working this page can honestly
       show. It restates the four answers the offer was looked up against, so
       the customer can check what was priced and say so if it is wrong: the
       trust function, kept. There is no base value, no component amount and
       no intermediate figure here, because there is no arithmetic in this
       browser to print and nothing behind the offer for it to draw on.
       Launch punch list S7, 27 Sep 2026: "70-step" removed from the line
       below. The 70-step inspection is what Upgrade does to the phones it
       sells; the seller's own phone is checked at a free inspection. */
    var det = document.createElement('details');
    det.className = 'ug-sell-working';
    var sum = document.createElement('summary');
    sum.textContent = TP('sell.what_we_priced');
    det.appendChild(sum);
    var tbl = h('table', 'ug-sell-table');
    // Edit everything (7 Oct 2026): the same words as the heading above the
    // offer (the section's "Heading above the cash offer") once she has
    // changed them, so one edit changes both. Unchanged (or served
    // untranslated on /ar), it is the word it always was: English 'Your cash
    // offer', the dictionary's Arabic on /ar.
    var tableHead = (wsNorm(TX('resultTitle')) !== asText(EN['sell.offer_title'])) ? TXH('resultTitle') : T('sell.offer_title');
    tbl.innerHTML = '<tbody><tr class="ug-sell-table__tot"><th>' + tableHead + '</th><td>' +
      fig(q.cash) + '</td></tr></tbody>';
    det.appendChild(tbl);
    det.appendChild(h('p', 'ug-sell-note', T('sell.working_note', { summary: pricedSummary(q) })));
    body.appendChild(det);

    /* booking CTA -- this is where Sell ends: cash, not a cart.

       The sell page mounts the booking section (sections/upgrade-book.liquid,
       templates/page.sell.json) directly below this tool, and setTradein()
       above has just put this quote into the shared state -- so that form now
       shows the device, the condition, the battery and this cash figure, and
       carries them into the contact email. Sending the seller there, rather
       than to a phone number, is the only path that captures the lead.
       Same detection as bookHref() in assets/upgrade-deal.js. Where no
       booking form is on the page, the confirmed hotline remains the
       fallback. */
    var ctaRow = h('div', 'ug-sell-cta-row');
    var bookBtn;

    if (document.querySelector('[data-ug-book]')) {
      // "Book my sale", not "inspection": since 28 Sep 2026 (LOG-021) a
      // trade-in ends in an order, so the booking form is the sell's alone and
      // the two journeys end in two differently named actions.
      bookBtn = h('a', 'ug-btn ug-btn--primary ug-btn--full', TXH('bookLabel'));
      bookBtn.href = '#book';
    } else {
      var bookingContact = buildContact(sellEnquiryMessage(q));
      bookBtn = h('a', 'ug-btn ug-btn--primary ug-btn--full',
        bookingContact.isWhatsapp ? T('sell.book_whatsapp_demo') : T('sell.book_call_cta', { hotline: HOTLINE }));
      bookBtn.href = bookingContact.href;
      if (bookingContact.isWhatsapp) {
        bookBtn.target = '_blank';
        bookBtn.rel = 'noopener';
        bookBtn.title = T('sell.whatsapp_demo_title');
      }
    }
    ctaRow.appendChild(bookBtn);
    body.appendChild(ctaRow);

    // Omar, 28 Sep 2026 (LOG-021): sellers are paid at the visit, in cash or
    // by transfer, whichever they want.
    var visitLine = TXH('visitLine');   // Lynkrs batch 2 (W1b): a setting; emptied, left out
    if (visitLine) body.appendChild(h('p', 'ug-sell-note', visitLine));

    // The sell journey above is complete and untouched: its own number, its
    // own working, its own booking button. The alternative follows it rather
    // than interrupting it.
    appendTradeInBridge(body, q, bonusRates, ups);

    body.appendChild(resetBtn());

    s.appendChild(body);
    return s;
  }

  /* Launch punch list S9 (3), 27 Sep 2026: "Start again" clears the published
     valuation as well as the answers, so the booking form below stops
     showing, and emailing, the old offer. */
  function resetBtn() {
    var reset = h('button', 'ug-sell-reset', TXH('resetLabel'));
    reset.type = 'button';
    reset.onclick = function () {
      S = { brand: null, series: null, storage: null, condition: null, faulty: null, battery: null, up: null, step: 1 };
      if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
      render();
    };
    return reset;
  }

  /* ---- boot ---------------------------------------------------------------*/
  fetch(root.dataset.upgradeSell)
    .then(function (r) { return r.json(); })
    .then(function (json) {
      DATA = json;
      // We have just fetched the file assets/upgrade-deal.js would otherwise
      // fetch a second time for the trade-up bonus rate. Hand the meta block
      // straight over: until it has a rate, UpgradeDeal.totals() returns null
      // and the booking summary on this page cannot paint a balance.
      if (window.UpgradeDeal && window.UpgradeDeal.setRates) window.UpgradeDeal.setRates(json.meta);
      render();
    })
    .catch(function () {
      root.innerHTML = '<p class="ug-sell-note">' + T('sell.pricing_unavailable') + '</p>';
    });

  /* ---- repaint when the live prices land ---------------------------------
     This tool PAINTS IMMEDIATELY and repaints; it never waits on the price
     map. Decision of 22 September 2026, the pattern assets/upgrade-catalogue.js
     already proves at its :273 listener: whenReady() can hold a consumer for
     the whole walk budget against a hanging endpoint, and a page that never
     shows a price is a worse outage than one that briefly shows fewer trade-up
     options. So the boot render above stays exactly where it is, immediately
     after setRates(), and this listener corrects the screen afterwards.

     Why this surface needs it more than most: an unpriced trade-up target is
     not drawn as a hotline card here, it is DROPPED (tradeUpOptions() returns
     early on a null price). Above the 50-product Liquid seed in
     snippets/upgrade-price-map.liquid, a device whose price only arrives with
     the walk would otherwise be silently missing from the bridge for the rest
     of the page's life -- no placeholder, nothing a customer could read as
     "ask us". render() re-derives the option list from scratch rather than
     redrawing the chosen cards, so the missing cards appear.

     Guards, in order:
     - no DATA means the offer file has not arrived (or failed): nothing has
       been painted yet, and the boot render above will read the settled map.
     - below step 4 nothing on screen is priced -- steps 1 to 3 are the four
       questions -- so there is nothing to correct and no reason to tear the
       DOM out from under a customer who is mid-answer.
     - the "What we priced" disclosure is reopened if the customer had it open,
       because a repaint they did not ask for must not collapse what they were
       reading.

     Everything here is feature-detected rather than assumed: the event may
     never fire (a price map without the walk), and document.addEventListener
     is checked before use. Nothing reads api.complete: the event is the
     signal, and a surface that tested `!api.complete` would treat a price map
     that simply does not publish the field as permanently unsettled. */
  function repaintForPrices() {
    if (!DATA) return;
    if (S.step < 4) return;

    var det = root.querySelector('details.ug-sell-working');
    var wasOpen = !!(det && det.open);

    render({ silent: true });

    if (wasOpen) {
      var again = root.querySelector('details.ug-sell-working');
      if (again) again.open = true;
    }
  }

  if (typeof document.addEventListener === 'function') {
    document.addEventListener('ug:prices:ready', repaintForPrices);
  }

  /* ---- the theme editor (Lynkrs batch 2, W1b, 6 Oct 2026) ---------------
     Each time the content lead changes a field of the "Upgrade sell" section
     (the tool's words are its settings since batch E2), the theme editor
     re-renders the section: the [data-upgrade-sell] mount comes back empty
     and its <script> tag is not run again, so the preview lost the whole
     valuation tool until a reload. On Shopify's shopify:section:load (fired
     only inside the theme editor, never for a customer: the same hook
     upgrade-pdp.js and upgrade-tradein.js use) this file runs once more
     against the new mount, so her words show at once. One listener per page
     (the flag), however many times the file has run. */
  if (typeof document.addEventListener === 'function' && typeof window !== 'undefined' && !window.__ugSellEditorHook) {
    window.__ugSellEditorHook = true;
    document.addEventListener('shopify:section:load', function (event) {
      var mount = (event && event.target && typeof event.target.querySelector === 'function')
        ? event.target.querySelector('[data-upgrade-sell]') : null;
      var me = document.querySelector('script[src*="upgrade-sell.js"]');
      if (!mount || !me || !document.body) return;
      var again = document.createElement('script');
      again.src = me.getAttribute('src');
      document.body.appendChild(again);
    });
  }
})();
